// Client/src/components/Layout.tsx
import { Suspense, memo } from 'react';
import { Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import GuestHeader from './GuestHeader';
import { useAuthStore } from '../store/authStore';
import { useTaskDetailsModal } from '../contexts/TaskDetailsModalContext';
import { lazyWithReload } from '../utils/lazyWithReload';

const TaskDetailsModal = lazyWithReload(() => import('./TaskDetailsModal'));

/* Props, die wir aus App.tsx erhalten */
interface LayoutProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

export default memo(function Layout({ sidebarOpen, toggleSidebar, closeSidebar }: LayoutProps) {
  const isCleaningStaffFn = useAuthStore((s) => s.isCleaningStaff);
  const isCleaningPortalUserFn = useAuthStore((s) => s.isCleaningPortalUser);
  const isAnonymousGuestFn = useAuthStore((s) => s.isAnonymousGuest);
  const { showModal, modalTask, closeModal } = useTaskDetailsModal();
  const isCleaningView = isCleaningStaffFn();
  const isPortalUser = isCleaningPortalUserFn();
  const isAnonymousGuest = isAnonymousGuestFn();
  const modalNode = showModal && modalTask ? (
    <Suspense fallback={null}>
      <TaskDetailsModal task={modalTask} onClose={closeModal} />
    </Suspense>
  ) : null;
  const routeFallback = (
    <div className="flex min-h-[40vh] items-center justify-center px-6 text-sm font-medium text-slate-500">
      Bereich wird geladen...
    </div>
  );
  const outletNode = (
    <Suspense fallback={routeFallback}>
      <Outlet />
    </Suspense>
  );

  if (isCleaningView) {
    return (
      <div
        className="flex flex-col bg-gray-50 safe-area-bottom"
        style={{ minHeight: 'var(--app-window-height)', height: 'var(--app-window-height)', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box' }}
      >
        <main className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0">
            {outletNode}
          </div>
        </main>
        {modalNode}
      </div>
    );
  }

  if (isAnonymousGuest) {
    return (
      <div className="flex h-[var(--app-window-height)] min-h-[var(--app-window-height)] flex-col overflow-hidden bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)]">
        <GuestHeader />
        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {outletNode}
        </main>
        {modalNode}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 overflow-hidden flex flex-col" style={{ minHeight: 'var(--app-window-height)', height: 'var(--app-window-height)', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box' }}>
      {/* Navbar nur für Nicht-Reinigungskräfte anzeigen */}
      {!isPortalUser && <Navbar toggleSidebar={toggleSidebar} />}

      {isPortalUser && (
        <div className="bg-white border-b shadow-sm px-4 py-3 flex items-center justify-between">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Menü öffnen"
          >
            <span className="font-semibold text-gray-700">Menü</span>
          </button>
          <div className="text-base font-semibold text-gray-900">Reinigung &amp; Personal</div>
          <div className="w-10" />
        </div>
      )}

  <div className="flex flex-1 overflow-hidden mt-1 min-h-0">
        <Sidebar sidebarOpen={sidebarOpen} closeSidebar={closeSidebar} />

        <main
          className={`
            flex-1 transition-all duration-300 px-1 sm:px-2 md:px-4
            ${sidebarOpen ? 'lg:ml-60 xl:ml-72' : ''}
            max-w-full overflow-hidden flex flex-col
          `}
        >
          <div className="flex-1 max-w-full overflow-y-auto overflow-x-hidden py-4" style={{ contain: 'layout style', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {outletNode}
          </div>
        </main>
      </div>

      {modalNode}
    </div>
  );
});
